function visibleSemanticIds(snapshot = {}) {
  return new Set(
    (snapshot?.sceneState?.visibleSemanticObjects || [])
      .map((object) => String(object?.id || "").toLowerCase())
      .filter(Boolean)
  );
}

function verificationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `semantic-${crypto.randomUUID()}`;
  }
  return `semantic-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function evaluateMaxxActionOutcome({ actionId, snapshot, isMobile = false } = {}) {
  const action = String(actionId || "").toLowerCase();
  const visible = visibleSemanticIds(snapshot);
  const activePortal = String(snapshot?.sceneState?.activePortal || "").toLowerCase();

  if (action === "pause_neurons") {
    const mobile =
      Boolean(isMobile) ||
      snapshot?.sceneState?.availableSemanticActions?.some(
        (entry) => entry?.id === "pause_neurons" && entry?.targetId === "maxx_neurons"
      );
    const passed = mobile || visible.has("maxx_neurodesign");
    return {
      status: passed ? "pass" : "pending",
      evidence: passed
        ? mobile
          ? ["maxx_neurons_paused_mobile"]
          : ["maxx_neurodesign_visible"]
        : [],
      observedEffects: passed
        ? mobile
          ? [{ type: "pause_neurons", target: "maxx_neurons" }]
          : [{ type: "pause_neurons" }, { type: "reveal_neurodesign", target: "maxx_neurodesign" }]
        : [],
    };
  }

  if (action === "resume_neurons") {
    const passed = !visible.has("maxx_neurodesign");
    return {
      status: passed ? "pass" : "pending",
      evidence: passed ? ["maxx_neurodesign_hidden"] : [],
      observedEffects: passed
        ? [{ type: "resume_neurons" }, { type: "hide_neurodesign", target: "maxx_neurodesign" }]
        : [],
    };
  }

  if (action === "back_to_root") {
    const passed = activePortal === "root";
    return {
      status: passed ? "pass" : "pending",
      evidence: passed ? ["root_portal_active"] : [],
      observedEffects: passed ? [{ type: "navigate_root", target: "root" }] : [],
    };
  }

  return { status: "unknown", evidence: [], observedEffects: [] };
}

export function beginMaxxActionVerification(actionId, metadata = {}) {
  if (typeof window === "undefined") return null;
  const verification = {
    verificationId: verificationId(),
    actionId: String(actionId || "").toLowerCase(),
    status: "pending",
    metadata,
    startedAt: new Date().toISOString(),
  };
  window.__pendingSemanticActionVerification = verification;
  window.dispatchEvent(new CustomEvent("semantic-action-verification-started", { detail: verification }));
  return verification;
}

export function completeMaxxActionVerification(
  actionId,
  { status, evidence = [], observedEffects = [], boundary = null } = {}
) {
  if (typeof window === "undefined") return null;
  const verification = {
    ...(window.__pendingSemanticActionVerification || {}),
    actionId: String(actionId || "").toLowerCase(),
    status: status || "unknown",
    evidence,
    observedEffects,
    boundary,
    completedAt: new Date().toISOString(),
  };
  window.__pendingSemanticActionVerification = null;
  window.__lastSemanticActionVerification = verification;
  window.dispatchEvent(new CustomEvent("semantic-action-verified", { detail: verification }));
  return verification;
}

export function setMaxxActionPersistenceReceipt({
  verificationId: expectedVerificationId = null,
  status,
  trajectoryId = null,
  mode = null,
  error = null,
} = {}) {
  if (typeof window === "undefined") return null;
  const current = window.__lastSemanticActionVerification;
  if (!current?.actionId) return null;
  if (expectedVerificationId && current.verificationId !== expectedVerificationId) return current;

  const verification = {
    ...current,
    persistence: {
      required: true,
      status: String(status || "unknown").toLowerCase(),
      trajectoryId: trajectoryId || null,
      mode: mode || null,
      error: error ? String(error).slice(0, 300) : null,
      updatedAt: new Date().toISOString(),
    },
  };

  window.__lastSemanticActionVerification = verification;
  if (window.__sceneObservationSnapshot?.sceneState) {
    window.__sceneObservationSnapshot.sceneState.semanticActionVerification = verification;
  }
  window.dispatchEvent(new CustomEvent("semantic-action-persistence-updated", { detail: verification }));
  return verification;
}

export function verifyPendingMaxxAction(snapshot) {
  if (typeof window === "undefined") return null;
  const pending = window.__pendingSemanticActionVerification;
  if (!pending?.actionId) return window.__lastSemanticActionVerification || null;

  const result = evaluateMaxxActionOutcome({ actionId: pending.actionId, snapshot });
  if (result.status !== "pass") return { ...pending, ...result };
  return completeMaxxActionVerification(pending.actionId, result);
}
